import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { categoriesAPI, adminAPI, mainCategoriesAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', image_url: '', main_category_id: '' });

  const fetchCategories = () => {
    setLoading(true);
    categoriesAPI.list()
      .then(r => setCategories(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCategories();
    mainCategoriesAPI.list().then(r => setMainCategories(r.data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', image_url: '', main_category_id: '' });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name,
      description: cat.description || '',
      image_url: cat.image_url || '',
      main_category_id: cat.main_category_id || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await adminAPI.updateCategory(editing.id, form);
        toast.success('Category updated');
      } else {
        await adminAPI.createCategory(form);
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await adminAPI.deleteCategory(id);
      toast.success('Category deleted');
      fetchCategories();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Categories</h1>
          <p className="font-body text-sm text-foreground/70 mt-1">{categories.length} categories</p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="add-category-btn"
          className="rounded-full bg-teal-dark border border-transparent text-white hover:bg-teal font-body text-sm gap-2"
        >
          <Plus className="h-4 w-4" /> Add Category
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 rounded-2xl bg-muted animate-pulse" />)}
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-muted/30 border border-border/50">
          <p className="font-heading text-xl text-foreground/50">No categories yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="categories-grid">
          {categories.map(cat => (
            <div
              key={cat.id}
              data-testid={`admin-category-${cat.id}`}
              className="flex gap-4 p-6 rounded-2xl bg-card border border-border"
            >
              <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                {cat.image_url ? (
                  <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <span className="font-heading text-xl text-foreground/40">{cat.name[0]}</span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-lg font-medium text-foreground">{cat.name}</h3>
                <p className="font-body text-sm text-foreground/60 truncate">{cat.description}</p>
                <p className="font-body text-xs text-foreground/40 mt-1">Slug: {cat.slug}</p>
              </div>
              <div className="flex items-start gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(cat)} className="h-8 w-8 hover:bg-muted rounded-full" data-testid={`edit-category-${cat.id}`}>
                  <Pencil className="h-3.5 w-3.5 text-foreground/70" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.id)} className="h-8 w-8 hover:bg-destructive/10 rounded-full" data-testid={`delete-category-${cat.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive/60" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background rounded-2xl max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              {editing ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-body text-sm text-foreground">Name *</Label>
              <Input data-testid="category-name-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="mt-1 rounded-xl bg-card border-border font-body" />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground">Description</Label>
              <Input data-testid="category-desc-input" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="mt-1 rounded-xl bg-card border-border font-body" />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground">Image URL</Label>
              <Input data-testid="category-image-input" value={form.image_url} onChange={e => setForm(f => ({...f, image_url: e.target.value}))} className="mt-1 rounded-xl bg-card border-border font-body" placeholder="https://..." />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground">Main Category</Label>
              <select
                data-testid="category-main-select"
                value={form.main_category_id}
                onChange={e => setForm(f => ({...f, main_category_id: e.target.value}))}
                className="mt-1 w-full rounded-xl bg-card border border-border px-3 py-2 font-body text-sm text-foreground"
              >
                <option value="">— None —</option>
                {[...mainCategories].sort((a,b)=>(a.name||'').localeCompare(b.name||'',undefined,{sensitivity:'base'})).map(mc => (
                  <option key={mc.id} value={mc.id}>{mc.name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full border-border font-body text-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-category-btn" className="rounded-full bg-teal-dark border border-transparent text-white hover:bg-teal font-body text-sm gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
